import { Component, OnInit } from "@angular/core";

import { ActivatedRoute, Router } from "@angular/router";

import { FormControl } from "@angular/forms";
import { SelectionType } from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";

import { BaseV2Component } from "../../base-module/base-component-v2/base-component-v2.component";
import { ActionDialog } from "../../base-module/base-component/base.component";

import { MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AuthService } from "app/base-module/base-service/auth.service";
import { TipoInvestimento } from "app/constants/tipo-investimento.constants";
import { CadastroBoletaExecucaoComponent } from "app/renda-variavel/boletas/cadastro-boleta-execucao/cadastro-boleta-execucao.component";
import { DIALOG_CANCEL_ACTION, DialogCancelAction } from "app/shared/dialog-constants";
import { NgxSortEvent } from "app/shared/types/ngx-datatable-custom";
import { UserRoleEnum } from "app/usuarios/models/user-role.enum";
import { Usuario } from "app/usuarios/models/user.model";
import { AtualizaStatusBoletaComponent } from "../atualiza-status-boleta/atualiza-status-boleta.component";
import { FiltrarBoletaComponent } from "../filtrar-boleta/filtrar-boleta.component";
import { FiltrosBoletaQueryParams } from "../models/filtros-boleta-query-params.model";
import { AtualizaStatusBoletaRequestModel } from "../models/lista-boleta/atualiza-status-boleta-request.model";
import { ListaBoletaQueryParams } from "../models/lista-boleta/lista-boleta-query-params.model";
import { MovimentacaoBoletaModel } from "../models/lista-boleta/movimentacao-boleta.model";
import { StatusBoletaRendaVariavelEnum } from "../models/status-boleta-renda-variavel.enum";
import { BoletaService } from "../services/boleta.service";

@Component({
    selector: "app-lista-boleta",
    templateUrl: "./lista-boleta.component.html",
    styleUrls: ["./lista-boleta.component.scss"],
})
export class ListaBoletaComponent
    extends BaseV2Component<MovimentacaoBoletaModel, ListaBoletaQueryParams>
    implements OnInit
{
    readonly SelectionType = SelectionType;
    readonly UserRole = UserRoleEnum;
    readonly StatusBoletaRendaVariavel = StatusBoletaRendaVariavelEnum;
    user: Usuario;
    search = new FormControl<string>("");
    inputSubscription!: Subscription;

    selected: MovimentacaoBoletaModel[] = [];
    titulo: string = "Boleta de Renda Variável";

    constructor(
        public service: BoletaService,
        public dialog: MatDialog,
        override snackBar: MatSnackBar,
        private route: Router,
        public _authService: AuthService,
        private activatedRoute: ActivatedRoute
    ) {
        super(dialog, snackBar);

        const strUser = this._authService.getUser();
        this.user = JSON.parse(strUser ? strUser : "{}");
        this.search.setValue("");
    }

    ngOnInit(): void {
        this.getAll();
    }

    /**
     * Retorna o número de filtros ativos
     * Exclui parâmetros de paginação
     * @returns Quantidade de filtros aplicados
     */
    get activeFiltersCount(): number {
        const availableParams = Object.keys(this.params).filter((param) => param !== "page" && param !== "per_page");
        if (availableParams.length === 0) return 0;

        // Usar constante centralizada dos campos de filtro
        const filterValues = Object.values(this.params).filter(
            (value) => value !== null && value !== undefined && value !== ""
        );

        return filterValues.length;
    }

    /**
     * Indica se há filtros ativos
     * Usado para exibir/ocultar botão de limpar filtros
     * @returns true se houver pelo menos um filtro ativo
     */
    get hasActiveFilters(): boolean {
        return this.activeFiltersCount > 0;
    }

    /**
     * Constrói objeto de filtros a partir do resultado do dialog
     * @param result - Resultado retornado pelo dialog de filtros
     * @returns Objeto com filtros formatados
     */
    private buildFiltrosFromResult(result: FiltrosBoletaQueryParams): FiltrosBoletaQueryParams {
        const cleanFilters = Object.fromEntries(
            Object.entries(result).filter(([_, value]) => value !== undefined && value !== null && value !== "")
        );
        return { ...cleanFilters };
    }

    /**
     * Persiste os filtros na URL como query parameters
     * @param filtros - Objeto com os filtros a serem persistidos
     */
    private persistFiltersInUrl(filtros: FiltrosBoletaQueryParams): void {
        // Remove propriedades vazias/undefined para limpar a URL
        const cleanFilters = Object.fromEntries(
            Object.entries(filtros).filter(([_, value]) => value !== undefined && value !== null && value !== "")
        );

        this.route.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: cleanFilters,
            replaceUrl: true,
        });
    }

    /**
     * Evento de ordenação da lista de produtos
     * @param event
     */
    onSort(event: NgxSortEvent): void {
        this.params.sortColumn = event.column.prop;

        if (event.column.name === "Responsavel Operação") {
            this.params.sortColumn = "operador_id";
        } else if (event.column.name === "Escritório") {
            this.params.sortColumn = "escritorio_id";
        } else if (event.column.name === "Conta Cliente") {
            this.params.sortColumn = "numero_xp";
        } else if (event.column.name === "Cliente") {
            this.params.sortColumn = "nome_cliente";
        } else if (event.column.name === "Status") {
            this.params.sortColumn = "status_id";
        }

        this.params.sortOrder = event.newValue;
        this.getAll();
    }

    /**
     * Evento da paginação
     * @param {PageEvent} event
     */
    pageEvent(event: PageEvent): void {
        this.params.page = event.pageIndex + 1;
        this.params.per_page = event.pageSize;

        this.getAll();
    }

    /**
     * Calcula o saldo da boleta subtraindo o valor executado do valor total
     * @param row - Dados da linha da boleta
     * @returns Saldo calculado
     */
    getSaldo(row: MovimentacaoBoletaModel): number {
        return parseFloat(row.valor) - parseFloat(row.vlr_executado);
    }

    /**
     * Consulta lista de Boletas
     */
    getAll(): void {
        if (this.search.value) {
            this.params.search = this.search.value;
        }

        this.loadingIndicator = true;
        this.inputSubscription = this.service.getAll(this.params).subscribe({
            next: (data) => {
                this.length = data.meta?.total ?? 0;
                this.rows = data.data;
            },
            error: (error) => {
                if (error.status === 401) {
                    this._authService.logout();
                    this.route.navigate(["/auth/login"]);
                } else {
                    this.showSnackMessage(
                        `Erro ao buscar boletas: ${error.error?.message || "Erro desconhecido"}`,
                        "error"
                    );
                }
                this.loadingIndicator = false;
            },
            complete: () => {
                this.loadingIndicator = false;
            },
        });
    }

    /**
     * Exclui registro de boleta
     * @param boleta - Dados da boleta a ser excluída
     */
    delete(boleta: MovimentacaoBoletaModel): void {
        if (!this.alterarBoleta(boleta)) {
            this.showSnackMessage("Você não tem permissão para excluir esta boleta", "error");
            return;
        }

        const valorBoleta = new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(this.exibeValor(boleta));

        this.dialog.open(ActionDialog, {
            width: "400px",
            disableClose: true,
            panelClass: "modal-delete",
            data: {
                title: "Excluir boleta",
                text: `Deseja realmente excluir a boleta do cliente ${boleta.nome_cliente} no valor de ${valorBoleta} ?`,
                callback: () => {
                    this.dialog.closeAll();

                    this.loadingIndicator = true;
                    this.service.delete(boleta.id).subscribe({
                        next: (data) => {
                            this.loadingIndicator = false;
                            this.showSnackMessage(data.message, "success");
                            this.getAll();
                        },
                        error: (error) => {
                            this.loadingIndicator = false;
                            this.showSnackMessage(
                                `Erro ao excluir boleta: ${error.error?.message || "Erro desconhecido"}`,
                                "error"
                            );
                        },
                        complete: () => {
                            this.loadingIndicator = false;
                        },
                    });
                },
            },
        });
    }

    /**
     * Exclui múltiplas boletas selecionadas
     */
    deleteAll(): void {
        if (!this._authService.isPermissao(["Excluir Boleta"])) {
            this.showSnackMessage("Você não tem permissão para excluir boletas", "error");
            return;
        }

        if (this.selected.length === 0) {
            this.showSnackMessage("Selecione pelo menos uma boleta para realizar a exclusão!", "error");
            return;
        }

        this.dialog.open(ActionDialog, {
            width: "250px",
            disableClose: true,
            panelClass: "modal-delete",
            data: {
                title: "Excluir boleta(s)",
                text: "Deseja realmente excluir este item?",
                callback: () => {
                    this.dialog.closeAll();

                    this.loadingIndicator = true;
                    this.service.deleteAll(this.selected).subscribe({
                        next: (data) => {
                            this.loadingIndicator = false;
                            this.showSnackMessage(data.message, "success");
                            this.getAll();
                        },
                        error: (error) => {
                            this.loadingIndicator = false;
                            this.showSnackMessage(
                                `Erro ao excluir boletas: ${error.error?.message || "Erro desconhecido"}`,
                                "error"
                            );
                        },
                        complete: () => {
                            this.loadingIndicator = false;
                        },
                    });
                },
            },
        });
    }

    /**
     * Abre a tela de cadastro de nova boleta
     * TODO: Verificar se o parâmetro tipo "1" ainda é necessário na rota de cadastro
     */
    openCadastro(): void {
        this.route.navigate(["/movimentacao/boleta/cadastro/1"]); // TODO: CHECK ID 1, IF NEEDS
    }

    /**
     * Abre a tela de edição de uma boleta existente
     * @param boletaID - ID da boleta a ser editada
     * TODO: Verificar se o parâmetro tipo "1" ainda é necessário na rota de cadastro
     */
    openAlterar(boletaID: number): void {
        this.route.navigate(["/movimentacao/boleta/cadastro/1/" + boletaID]);
    }

    /**
     * Modal de Filtro
     */
    openFiltro(): void {
        // Preparar filtros iniciais a partir dos params atuais
        const initialFilters: FiltrosBoletaQueryParams = {
            data_operacao_inicio: this.params.data_operacao_inicio,
            data_operacao_fim: this.params.data_operacao_fim,
            cliente_id: this.params.cliente_id,
            assessor_id: this.params.assessor_id,
            escritorio_id: this.params.escritorio_id,
            status_id: this.params.status_id,
            acao_comercial_id: this.params.acao_comercial_id,
            primeiro_contato_id: this.params.primeiro_contato_id,
            estrategia_id: this.params.estrategia_id,
            homebroker: this.params.homebroker,
            operador_id: this.params.operador_id,
        };

        const dialogRef = this.dialog.open(FiltrarBoletaComponent, {
            width: "700px",
            maxHeight: "99vh",
            data: {
                titulo: "Filtrar Boleta",
                status_boleta: true,
                status_ordem: false,
                tipo: TipoInvestimento.RENDA_VARIAVEL,
                initialFilters,
            },
        });

        dialogRef.afterClosed().subscribe((result: DialogCancelAction | FiltrosBoletaQueryParams | undefined) => {
            if (result === DIALOG_CANCEL_ACTION) return;

            if (result) {
                const filtros = this.buildFiltrosFromResult(result);

                this.params = { ...this.params, ...filtros };

                this.persistFiltersInUrl(filtros);

                // Resetar paginação e buscar
                this.params.page = 1;
                this.getAll();
            }
        });
    }

    /**
     * Limpa todos os filtros aplicados
     * Preserva apenas paginação
     * @returns void
     */
    clearFiltro(): void {
        this.params = {
            page: 1,
            per_page: this.pageSize || 100,
        };

        this.route.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {},
            replaceUrl: true,
        });

        this.params.page = 1;
        this.getAll();

        this.snackBar.open("Filtros limpos com sucesso!", "Fechar", {
            duration: 3000,
            panelClass: ["success-snackbar"],
        });
    }

    /**
     * Manipula a seleção de itens na tabela
     * @param selectedEvent - Evento contendo os itens selecionados
     */
    onSelect(selectedEvent: { selected: MovimentacaoBoletaModel[] }): void {
        this.selected = [...selectedEvent.selected];
    }

    /**
     * Abre modal para atualizar status das boletas selecionadas
     */
    openAtualizar(): void {
        if (this.selected.length === 0) {
            this.showSnackMessage("Selecione pelo menos uma boleta para realizar a atualização do status", "error");
            return;
        }

        const dialogRef = this.dialog.open(AtualizaStatusBoletaComponent, {
            width: "700px",
            data: {
                boletas: this.selected,
                tipo: TipoInvestimento.RENDA_VARIAVEL,
            },
        });

        dialogRef.afterClosed().subscribe((result: DialogCancelAction | undefined) => {
            if (result === DIALOG_CANCEL_ACTION) return;

            if (result) {
                this.getAll();
                this.selected = [];
            }
        });
    }

    /**
     * Exporta a lista de boletas para arquivo Excel
     */
    getAllExport(): void {
        this.openLoading();
        this.service.getAllExport(this.params).subscribe({
            next: (data) => {
                const blob = new Blob([data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const url = window.URL.createObjectURL(blob);
                window.open(url, "_blank");
            },
            error: (error) => {
                this.closeLoading();
                this.showSnackMessage(
                    `Erro ao exportar boletas: ${error.error?.message || "Erro desconhecido"}`,
                    "error"
                );
            },
            complete: () => {
                this.closeLoading();
            },
        });
    }

    /**
     * Verifica se a boleta pode ser alterada baseado em permissões e status
     * @param row - Dados da linha da boleta
     * @returns true se a boleta pode ser alterada, false caso contrário
     */
    alterarBoleta(row: MovimentacaoBoletaModel): boolean {
        const adminRoles = [
            UserRoleEnum.ADMINISTRADOR_SISTEMAS,
            UserRoleEnum.SOCIO_OPERADOR_RENDA_VARIAVEL,
            UserRoleEnum.SOCIO_DIRETOR_ESPECIALISTA_RENDA_VARIAVEL,
        ];
        const isUserAdmin = adminRoles.includes(this.user.role_id);

        if (row.bloqueio && !isUserAdmin) {
            return false;
        }

        const editableStatuses = [
            StatusBoletaRendaVariavelEnum.BOLETA_MRV,
            StatusBoletaRendaVariavelEnum.PRIMEIRA_OPERACAO,
            StatusBoletaRendaVariavelEnum.COE_ENVIADO_CLIENTE,
            StatusBoletaRendaVariavelEnum.SALDO_INDISPONIVEL,
            StatusBoletaRendaVariavelEnum.PUSH_DESABILITADO,
            StatusBoletaRendaVariavelEnum.RECUSA_DA_ORDEM,
            StatusBoletaRendaVariavelEnum.REUNIAO_1_EFETIVADA_DADOS,
        ];

        if (!isUserAdmin && !editableStatuses.includes(row.status_id)) {
            return false;
        }

        return true;
    }

    /**
     * Aprova uma boleta específica
     * @param id - ID da boleta a ser aprovada
     */
    aprovar(id: number): void {
        this.openLoading();

        const payload: AtualizaStatusBoletaRequestModel = {
            status_id: StatusBoletaRendaVariavelEnum.BOLETA_MRV,
            boletas: [{ id }],
        };

        this.service.atualizaStatus(payload).subscribe({
            next: (data) => {
                this.closeLoading();
                this.showSnackMessage(data.message, "success");
            },
            error: (error) => {
                this.closeLoading();
                this.showSnackMessage(
                    `Erro ao aprovar boleta: ${error.error?.message || "Erro desconhecido"}`,
                    "error"
                );
            },
            complete: () => {
                this.closeLoading();
                this.getAll();
            },
        });
    }

    /**
     * Aprova múltiplas boletas selecionadas
     */
    aprovarBoletas(): void {
        if (this.selected.length === 0) {
            this.showSnackMessage("Selecione pelo menos uma boleta para realizar a aprovação", "error");
            return;
        }

        this.dialog.open(ActionDialog, {
            width: "250px",
            disableClose: true,
            data: {
                title: "Aprovar boleta",
                text: "Deseja realmente aprovar as boletas selecionadas ?",
                callback: () => {
                    this.openLoading();

                    const payload: AtualizaStatusBoletaRequestModel = {
                        status_id: StatusBoletaRendaVariavelEnum.BOLETA_MRV,
                        boletas: this.selected.map((boleta) => ({ id: boleta.id })),
                    };

                    this.service.atualizaStatus(payload).subscribe({
                        next: (data) => {
                            this.closeLoading();
                            this.showSnackMessage(data.message, "success");
                        },
                        error: (error) => {
                            this.closeLoading();
                            this.showSnackMessage(
                                `Erro ao aprovar boletas: ${error.error?.message || "Erro desconhecido"}`,
                                "error"
                            );
                        },
                        complete: () => {
                            this.closeLoading();
                            this.getAll();
                            this.selected = [];
                        },
                    });
                },
            },
        });
    }

    /**
     * Calcula o valor a ser exibido da boleta baseado no status e valor executado
     * @param row - Dados da linha da boleta
     * @returns Valor calculado para exibição
     */
    exibeValor(row: MovimentacaoBoletaModel): number {
        if (
            parseFloat(row.vlr_executado) < parseFloat(row.valor) + row.valor_total &&
            parseFloat(row.vlr_executado) > 0 &&
            row.status_id === StatusBoletaRendaVariavelEnum.OPERACAO_EFETIVADA
        ) {
            return parseFloat(row.vlr_executado);
        } else {
            return parseFloat(row.valor) + row.valor_total;
        }
    }

    /**
     * Bloqueia ou desbloqueia boletas
     * @param tipo - Tipo da operação: 'B' para bloquear, 'D' para desbloquear
     */
    bloqueiarBoletas(tipo: "B" | "D"): void {
        if (!this._authService.isPermissao(["Bloqueiar Boletas"])) {
            this.showSnackMessage("Você não tem permissão para bloquear boletas", "error");
            return;
        }

        this.openLoading();

        const status = tipo === "B" ? true : false;

        this.service.bloqueio({ bloqueio: status }).subscribe({
            next: (data) => {
                this.closeLoading();
                this.showSnackMessage(data.message, "success");
            },
            error: (error) => {
                this.closeLoading();
                this.showSnackMessage(
                    `Erro ao ${status ? "bloquear" : "desbloquear"} boletas: ${
                        error.error?.message || "Erro desconhecido"
                    }`,
                    "error"
                );
            },
            complete: () => {
                this.closeLoading();
                this.getAll();
            },
        });
    }

    /**
     * Cancela a subscription de busca em andamento
     */
    stopNow(): void {
        this.inputSubscription.unsubscribe();
    }

    /**
     * Manipula o evento de busca ao pressionar Enter
     * @param value - Valor digitado no campo de busca
     */
    onEnter(value: string): void {
        this.stopNow();
        this.params.search = value;
        this.search.setValue(value);
        this.getAll();
    }

    /**
     * Abre modal de cadastro de boleta de execução
     * @param boleta_id - ID da boleta para criar execução
     */
    openCadastroBoletaExecucacao(boleta_id: number): void {
        const dialogRef = this.dialog.open(CadastroBoletaExecucaoComponent, {
            width: "800px",
            maxHeight: "99vh",
            data: {
                id: null,
                boleta_id,
            },
        });

        dialogRef.afterClosed().subscribe((result: DialogCancelAction | undefined) => {
            if (result === DIALOG_CANCEL_ACTION) return;

            if (result) {
                this.getAll();
            }
        });
    }

    /**
     * Retorna a cor hexadecimal da estratégia para exibição da bolinha
     * Mapeia cor_hexadecimal (classe CSS) para valor hexadecimal real
     * @param row - Dados da linha da tabela
     * @returns Cor em formato hexadecimal (ex: #039be5)
     */
    getEstrategiaColor(row: MovimentacaoBoletaModel): string {
        return row.cor_hexadecimal || "#cccccc";
    }
}
